
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  defaultLanguage,
  LANGUAGE_STORAGE_KEY,
  supportedLanguageCodes,
  supportedLanguages,
} from '../i18n/config';
import { translate } from '../i18n';
import languageDirection from '../i18n/helpers/languageDirection';
import languageFallback from '../i18n/helpers/languageFallback';

const LanguageContext = createContext(null);

// Get the saved language from local storage.
const getInitialLanguage = () => {
  const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);

  return languageFallback(savedLanguage, defaultLanguage);
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);

  const direction = useMemo(() => {
    return languageDirection(language);
  }, [language]);

  // Update document language and direction.
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    document.body.dir = direction;

    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language, direction]);

  // Change the current interface language.
  const changeLanguage = useCallback((nextLanguage) => {
    if (!supportedLanguageCodes.includes(nextLanguage)) {
      return;
    }

    setLanguage(nextLanguage);
  }, []);

  // Translate a static frontend key.
  const t = useCallback((key, params = {}) => {
    return translate(key, language, params);
  }, [language]);

  const value = useMemo(
    () => ({
      t,
      language,
      direction,
      changeLanguage,
      supportedLanguages,
      isRtl: direction === 'rtl',
    }),
    [t, language, direction, changeLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// Use the language context safely.
export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }

  return context;
};
